/* Google Maps view for /brokerages — sibling of OpenHouseMap.
 *
 * Brokerages don't have events or visited state, so the popup is simpler:
 * name + address + realtor count + Add/Remove from Route.
 *
 * Pin colors:
 *   red `#C8102E`   default
 *   emerald `#10B981` selected for route
 *   gray  `#94A3B8`  geocode failed / not yet looked up
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  MarkerClusterer,
  InfoWindow,
  DirectionsRenderer,
  Rectangle,
} from "@react-google-maps/api";
import { Button } from "@/components/ui/button";
import { Plus, Minus, ExternalLink, AlertTriangle, Users, BoxSelect } from "lucide-react";

const SASKATOON_CENTER = { lat: 52.1332, lng: -106.67 };
const LIBRARIES = ["places"];

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const MAP_OPTIONS = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  zoomControl: true,
  gestureHandling: "greedy",
  clickableIcons: false,
};

function pinSymbol(color) {
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 9,
    fillColor: color,
    fillOpacity: 1,
    strokeWeight: 2.5,
    strokeColor: "#ffffff",
  };
}

function teardropPin(color) {
  return {
    path: "M 0,0 C -2,-20 -10,-22 -10,-30 a 10,10 0 1,1 20,0 c 0,8 -8,10 -10,30 z",
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
    scale: 1.4,
    anchor: new window.google.maps.Point(0, 0),
  };
}

export default function BrokerageMap({
  brokerages,
  selectedIds,
  onToggleSelection,
  onAreaSelect,
  onShowRealtors,
  homeCoords,
  directions,
}) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    id: "gmaps-script",
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });
  const [activeId, setActiveId] = useState(null);
  const [lassoMode, setLassoMode] = useState(false);
  const [drawStart, setDrawStart] = useState(null); // {lat, lng} of mouse down
  const [drawEnd, setDrawEnd] = useState(null);     // {lat, lng} of current drag
  const mapRef = useRef(null);
  const boundsFitDoneRef = useRef(false);

  const points = useMemo(
    () =>
      (brokerages || []).filter(
        (b) =>
          typeof b.lat === "number" &&
          typeof b.lng === "number" &&
          !Number.isNaN(b.lat) &&
          !Number.isNaN(b.lng),
      ),
    [brokerages],
  );
  const noGeo = (brokerages || []).length - points.length;
  const defaultCenter = homeCoords || SASKATOON_CENTER;

  const fitSig = useMemo(() => {
    if (!points.length) return "empty";
    return `${points.length}:${points[0]?.id}:${points[points.length - 1]?.id}:${homeCoords ? `${homeCoords.lat.toFixed(3)},${homeCoords.lng.toFixed(3)}` : "nohome"}`;
  }, [points, homeCoords]);

  const doFitBounds = useCallback(
    (map) => {
      if (!window.google) return;
      if (points.length === 0) {
        map.setCenter(defaultCenter);
        map.setZoom(12);
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      for (const p of points) bounds.extend({ lat: p.lat, lng: p.lng });
      if (homeCoords) bounds.extend(homeCoords);
      map.fitBounds(bounds, 64);
      if (points.length === 1) map.setZoom(14);
    },
    [points, homeCoords, defaultCenter],
  );

  const onLoadMap = useCallback(
    (map) => {
      mapRef.current = map;
      doFitBounds(map);
      boundsFitDoneRef.current = true;
    },
    [doFitBounds],
  );

  useEffect(() => {
    if (!mapRef.current || !boundsFitDoneRef.current) return;
    doFitBounds(mapRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitSig]);

  // Handle manual rectangle selection via mouse events
  const onMapMouseDown = useCallback(
    (e) => {
      if (!lassoMode || !e.latLng) return;
      setDrawStart({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      setDrawEnd({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    },
    [lassoMode],
  );

  const onMapMouseMove = useCallback(
    (e) => {
      if (!lassoMode || !drawStart || !e.latLng) return;
      setDrawEnd({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    },
    [lassoMode, drawStart],
  );

  const onMapMouseUp = useCallback(
    (e) => {
      if (!lassoMode || !drawStart || !onAreaSelect) {
        setDrawStart(null);
        setDrawEnd(null);
        return;
      }
      const end = e.latLng
        ? { lat: e.latLng.lat(), lng: e.latLng.lng() }
        : drawEnd;
      if (!end) {
        setDrawStart(null);
        setDrawEnd(null);
        return;
      }
      // Build bounds from the two corners
      const north = Math.max(drawStart.lat, end.lat);
      const south = Math.min(drawStart.lat, end.lat);
      const east = Math.max(drawStart.lng, end.lng);
      const west = Math.min(drawStart.lng, end.lng);
      // Select all points inside
      const idsInBounds = points.filter(
        (b) => b.lat >= south && b.lat <= north && b.lng >= west && b.lng <= east,
      ).map((b) => b.id);
      if (idsInBounds.length > 0) {
        onAreaSelect(idsInBounds);
      }
      setDrawStart(null);
      setDrawEnd(null);
      setLassoMode(false);
    },
    [lassoMode, drawStart, drawEnd, points, onAreaSelect],
  );

  // Rectangle bounds for the visual overlay while drawing
  const drawBounds = useMemo(() => {
    if (!drawStart || !drawEnd) return null;
    return {
      north: Math.max(drawStart.lat, drawEnd.lat),
      south: Math.min(drawStart.lat, drawEnd.lat),
      east: Math.max(drawStart.lng, drawEnd.lng),
      west: Math.min(drawStart.lng, drawEnd.lng),
    };
  }, [drawStart, drawEnd]);

  if (loadError) {
    return (
      <section
        className="rounded-lg border border-red-200 bg-red-50 p-6"
        data-testid="brokerage-map-load-error"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-red-900">
              Google Maps failed to load
            </div>
            <p className="text-xs text-red-700 mt-1">
              {String(loadError.message || loadError)}
            </p>
          </div>
        </div>
      </section>
    );
  }
  if (!apiKey) {
    return (
      <section
        className="rounded-lg border border-amber-200 bg-amber-50 p-6"
        data-testid="brokerage-map-no-key"
      >
        <div className="text-sm font-semibold text-amber-900">Map view unavailable</div>
        <p className="text-xs text-amber-700 mt-1">
          Set REACT_APP_GOOGLE_MAPS_API_KEY in /app/frontend/.env to enable the map.
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-lg border border-slate-200 overflow-hidden bg-white"
      data-testid="brokerage-map-view"
    >
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-slate-600">
          <span className="font-semibold text-slate-900 tabular-nums">{points.length}</span>{" "}
          mapped ·{" "}
          <span className="text-emerald-700 font-medium">{selectedIds.size}</span>{" "}
          on route
          {noGeo > 0 ? (
            <span className="ml-2 text-amber-700">· {noGeo} need address sync</span>
          ) : null}
        </span>
        <div className="flex items-center gap-2">
          {onAreaSelect ? (
            <Button
              variant={lassoMode ? "default" : "outline"}
              size="sm"
              onClick={() => setLassoMode((v) => !v)}
              className={`h-7 text-[11px] px-2 ${lassoMode ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
              data-testid="brokerage-map-lasso-toggle"
            >
              <BoxSelect className="h-3.5 w-3.5 mr-1" />
              {lassoMode ? "Drawing… click & drag" : "Area select"}
            </Button>
          ) : null}
          <LegendBar />
        </div>
      </div>
      <div
        className="relative w-full"
        style={{ height: "60vh", minHeight: "440px", maxHeight: "640px" }}
        data-testid="brokerage-map-container"
      >
        {!isLoaded ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500">
            Loading map…
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={defaultCenter}
            zoom={12}
            onLoad={onLoadMap}
            options={{
              ...MAP_OPTIONS,
              draggable: !lassoMode,
              draggableCursor: lassoMode ? "crosshair" : undefined,
            }}
            onMouseDown={onMapMouseDown}
            onMouseMove={onMapMouseMove}
            onMouseUp={onMapMouseUp}
          >
            {/* Visual rectangle while drawing */}
            {drawBounds ? (
              <Rectangle
                bounds={drawBounds}
                options={{
                  fillColor: "#10B981",
                  fillOpacity: 0.15,
                  strokeColor: "#10B981",
                  strokeWeight: 2,
                  clickable: false,
                }}
              />
            ) : null}

            {homeCoords ? (
              <Marker
                position={homeCoords}
                icon={pinSymbol("#0EA5E9")}
                title="Your start point"
                zIndex={9999}
              />
            ) : null}

            {directions ? (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  suppressInfoWindows: true,
                  preserveViewport: true,
                  polylineOptions: {
                    strokeColor: "#10B981",
                    strokeOpacity: 0.85,
                    strokeWeight: 5,
                  },
                }}
              />
            ) : null}

            <MarkerClusterer
              options={{ gridSize: 50, maxZoom: 13, styles: clusterStyles() }}
            >
              {(clusterer) =>
                points.map((b) => {
                  const isSelected = selectedIds.has(b.id);
                  const color = isSelected
                    ? "#10B981"
                    : b.geocode_status === "ok"
                    ? "#C8102E"
                    : "#94A3B8";
                  return (
                    <Marker
                      key={b.id}
                      position={{ lat: b.lat, lng: b.lng }}
                      clusterer={clusterer}
                      icon={teardropPin(color)}
                      onClick={() => setActiveId(b.id)}
                      data-testid={`brokerage-map-pin-${b.id}`}
                    />
                  );
                })
              }
            </MarkerClusterer>

            {activeId
              ? (() => {
                  const b = points.find((p) => p.id === activeId);
                  if (!b) return null;
                  const isSelected = selectedIds.has(b.id);
                  return (
                    <InfoWindow
                      position={{ lat: b.lat, lng: b.lng }}
                      onCloseClick={() => setActiveId(null)}
                      options={{ pixelOffset: new window.google.maps.Size(0, -28) }}
                    >
                      <PinPopup
                        brokerage={b}
                        isSelected={isSelected}
                        onToggleSelection={() => onToggleSelection(b.id)}
                        onShowRealtors={onShowRealtors ? () => { setActiveId(null); onShowRealtors(b); } : null}
                      />
                    </InfoWindow>
                  );
                })()
              : null}
          </GoogleMap>
        )}
      </div>
    </section>
  );
}

function PinPopup({ brokerage: b, isSelected, onToggleSelection, onShowRealtors }) {
  return (
    <div
      className="text-xs leading-snug"
      style={{ minWidth: 220, maxWidth: 280 }}
      data-testid={`brokerage-map-popup-${b.id}`}
    >
      <div className="font-semibold text-slate-900 text-sm">{b.name}</div>
      {b.office_address ? (
        <div className="text-slate-500 mt-1">{b.office_address}</div>
      ) : (
        <div className="text-slate-400 italic mt-1">No address on file</div>
      )}
      {onShowRealtors ? (
        <button
          type="button"
          onClick={onShowRealtors}
          className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700 hover:bg-slate-200 hover:text-slate-900 transition-colors cursor-pointer"
          data-testid={`brokerage-map-realtors-btn-${b.id}`}
        >
          <Users className="h-3 w-3" /> {b.realtor_count || 0} realtor{b.realtor_count === 1 ? "" : "s"}
        </button>
      ) : (
        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
          <Users className="h-3 w-3" /> {b.realtor_count || 0} realtor{b.realtor_count === 1 ? "" : "s"}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={onToggleSelection}
          className="h-8 px-2 text-xs text-white"
          style={{ background: isSelected ? "#10B981" : "hsl(var(--brand))" }}
          data-testid={`brokerage-map-toggle-route-${b.id}`}
        >
          {isSelected ? (
            <>
              <Minus className="h-3 w-3 mr-1" /> Remove
            </>
          ) : (
            <>
              <Plus className="h-3 w-3 mr-1" /> Add to route
            </>
          )}
        </Button>
        {b.website_url ? (
          <a
            href={b.website_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:underline self-center"
          >
            <ExternalLink className="h-3 w-3" /> Website
          </a>
        ) : null}
      </div>
    </div>
  );
}

function LegendBar() {
  return (
    <div className="flex items-center gap-3 text-[11px] text-slate-500">
      <span className="inline-flex items-center gap-1">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#C8102E" }} />
        Office
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#10B981" }} />
        On route
      </span>
      <span className="hidden sm:inline-flex items-center gap-1">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#94A3B8" }} />
        No address
      </span>
    </div>
  );
}

function clusterStyles() {
  const make = (size, hex) => ({
    textColor: "#ffffff",
    textSize: 13,
    height: size,
    width: size,
    url:
      "data:image/svg+xml;utf-8," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${hex}" stroke="#fff" stroke-width="2"/>
        </svg>`,
      ),
  });
  return [make(36, "#C8102E"), make(44, "#9A0B22"), make(52, "#6A0617")];
}
